// src/pages/suppliers/useSuppliersPage.ts
import { useEffect, useMemo, useState } from "react";

import { useSuppliers } from "@/api/features/suppliers/queries";
import {
  useDeleteSupplierMutation,
  useApproveSupplierMutation,
  useRejectSupplierMutation,
} from "@/api/features/suppliers/mutations";

import type {
  SupplierResult,
  CreateSupplierBody,
  UpdateSupplierBody,
  SupplierWithRelations,
  SupplierReservationContact,
  SupplierAccountContact,
  SupplierCallCentreContact,
} from "@/api/features/suppliers/types";
import type { Product } from "@/api/features/products/types";

/* ============================================================================
   Types – UI-level shapes (for table + forms)
============================================================================ */

export type ReservationContact = { name: string; mobile: string; email: string };
export type AccountContact = { name: string; mobile: string; email: string };
export type CallCentreContact = {
  callCentreNumber: string;
  callCentreName?: string;
  emergencyNumber: string;
  notes?: string;
};
export type OwnerDetails = { name: string; mobile: string; email: string };

export type LoginInfo = { note?: string; ownerUsername?: string | null };

export type CompanyDetails = {
  addressLine1: string;
  addressLine2: string;
  pincode: string;
  website: string;
  country: string;
  state: string;
  city: string;
  loginId: string;
  password: string;
};

/**
 * Form-level shape used by CreateSupplierForm / UpdateSupplierForm.
 * This is richer than the API bodies; we map it to CreateSupplierBody / UpdateSupplierBody.
 */
export type SupplierFormData = {
  supplierCompanyName: string;
  managerFirstName: string;
  managerLastName: string;
  managerEmail: string;
  managerMobile: string;
  product?: string;
  reservations: ReservationContact[];
  accounts: AccountContact[];
  callCentres: CallCentreContact[];
  owner: OwnerDetails;
  login: LoginInfo;
  company: CompanyDetails;
  active?: boolean; // derived from deletedAt on API side (for now)

  /** Product ids used by forms (multi-select) */
  productIds?: number[];
};

/**
 * Row used by SupplierTable.
 * This is a thin projection from SupplierResult.
 */
export type SupplierRow = {
  id: number;

  companyName: string;
  product?: string; // placeholder for future products linkage
  managerFirstName: string;
  managerLastName: string;

  reservationEmail?: string;
  emergencyPhone?: string;
  addressLine1?: string;
  addressLine2?: string;
  pincode?: string;
  country?: string;
  state?: string;
  city?: string;
  status: "PENDING" | "APPROVED" | "REJECTED";

  active: boolean;
};

export type SupplierFilters = {
  company?: string;
  product?: string;
  manager?: string;
  reservationEmail?: string;
  emergencyPhone?: string;
  addressLine1?: string;
  addressLine2?: string;
  pincode?: string;
  country?: string;
  state?: string;
  city?: string;
  status?: "PENDING" | "APPROVED" | "REJECTED" | "";
};

/* ============================================================================
   Mapping helpers – API ↔ UI
============================================================================ */

/** 🔹 Map API supplier → UI table row */
function mapSupplierToRow(s: SupplierResult): SupplierRow {
  return {
    id: s.id,
    companyName: s.companyName || s.supplierCompanyName,
    product: "", // can be derived later from products[] if needed
    managerFirstName: s.managerFirstName,
    managerLastName: s.managerLastName,
    reservationEmail: s.ownerEmail ?? undefined, // placeholder
    emergencyPhone: s.ownerMobile ?? undefined, // placeholder
    addressLine1: s.companyAddressLine1 ?? undefined,
    addressLine2: s.companyAddressLine2 ?? undefined,
    pincode: s.companyPostalCode ?? undefined,
    country: s.companyCountry ?? undefined,
    state: s.companyState ?? undefined,
    city: s.companyCity ?? undefined,
    status: (s.status as any) || "APPROVED",
    active: !s.deletedAt,
  };
}

/** 🔹 Map API supplier → form shape for Edit page (base, no relations) */
export function mapSupplierToForm(s: SupplierResult): SupplierFormData {
  const ownerFirst = (s.ownerFirstName ?? "").trim();
  const ownerLast = (s.ownerLastName ?? "").trim();
  const ownerName = [ownerFirst, ownerLast].filter(Boolean).join(" ").trim();

  return {
    supplierCompanyName: s.supplierCompanyName,
    managerFirstName: s.managerFirstName,
    managerLastName: s.managerLastName,
    managerEmail: s.managerEmail ?? "",
    managerMobile: s.managerMobile ?? "",
    product: "",

    // Contacts will be overridden by deep mapping if used
    reservations: [{ name: "", mobile: "", email: "" }],
    accounts: [{ name: "", mobile: "", email: "" }],
    callCentres: [
      {
        callCentreNumber: "",
        emergencyNumber: "",
        callCentreName: "",
        notes: "",
      },
    ],

    owner: {
      name: ownerName,
      mobile: s.ownerMobile ?? "",
      email: s.ownerEmail ?? "",
    },

    login: {
      note: s.loginNote ?? "",
      ownerUsername: s.ownerUsername ?? null,
    },

    company: {
      addressLine1: s.companyAddressLine1 ?? "",
      addressLine2: s.companyAddressLine2 ?? "",
      pincode: s.companyPostalCode ?? "",
      website: s.companyWebsite ?? "",
      country: s.companyCountry ?? "",
      state: s.companyState ?? "",
      city: s.companyCity ?? "",
      loginId: s.portalLoginId ?? "",
      password: s.portalPassword ?? "",
    },

    active: !s.deletedAt,
    productIds: [],
  };
}

/**
 * 🔹 Map SupplierWithRelations → full form shape for Edit page.
 *
 * Use this in your EditSupplier page:
 *   const { data: supplier } = useSupplier(id); // returns SupplierWithRelations
 */
export function mapSupplierDeepToForm(
  s: SupplierWithRelations,
  opts?: {
    products?: Product[];
    reservations?: SupplierReservationContact[];
    accounts?: SupplierAccountContact[];
    callCentres?: SupplierCallCentreContact[];
  }
): SupplierFormData & { productIds: number[] } {
  const base = mapSupplierToForm(s);

  // Prefer explicit opts if passed, otherwise use relations from `s`
  const products = opts?.products ?? s.products ?? [];
  const reservations = opts?.reservations ?? s.reservationContacts ?? [];
  const accounts = opts?.accounts ?? s.accountContacts ?? [];
  const callCentres = opts?.callCentres ?? s.callCentreContacts ?? [];

  const uiReservations =
    reservations.length > 0
      ? reservations.map((r) => ({
          name: r.name ?? "",
          mobile: r.phone ?? "",
          email: r.email ?? "",
        }))
      : base.reservations;

  const uiAccounts =
    accounts.length > 0
      ? accounts.map((a) => ({
          name: a.name ?? "",
          mobile: a.phone ?? "",
          email: a.email ?? "",
        }))
      : base.accounts;

  const uiCallCentres =
    callCentres.length > 0
      ? callCentres.map((c) => ({
          callCentreNumber: c.phone ?? "",
          callCentreName: c.name ?? "",
          emergencyNumber: "", // no dedicated field on API yet
          notes: c.notes ?? "",
        }))
      : base.callCentres;

  const productIds = products.map((p) => p.id);

  return {
    ...base,
    product: base.product ?? "",
    reservations: uiReservations,
    accounts: uiAccounts,
    callCentres: uiCallCentres,
    productIds,
  };
}

/* ============================================================================
   Helpers for username + deep payload mapping
============================================================================ */

function deriveOwnerNames(ownerFullName: string) {
  const trimmed = (ownerFullName || "").trim();
  if (!trimmed) return { ownerFirstName: undefined, ownerLastName: undefined };

  const parts = trimmed.split(" ").filter(Boolean);
  const ownerFirstName = parts[0];
  const ownerLastName = parts.slice(1).join(" ") || undefined;

  return { ownerFirstName, ownerLastName };
}

function mapFormContactsToDeep(form: SupplierFormData) {
  const reservations = (form.reservations ?? [])
    .filter((r) => (r.name || r.mobile || r.email).trim() !== "")
    .map((r) => ({
      // no id on form yet → all treated as new rows for now (deep sync recreates)
      name: r.name.trim(),
      email: r.email ? r.email.trim() : null,
      phone: r.mobile ? r.mobile.trim() : null,
      notes: null as string | null,
    }));

  const accounts = (form.accounts ?? [])
    .filter((a) => (a.name || a.mobile || a.email).trim() !== "")
    .map((a) => ({
      name: a.name.trim(),
      email: a.email ? a.email.trim() : null,
      phone: a.mobile ? a.mobile.trim() : null,
      notes: null as string | null,
    }));

  const callCentres = (form.callCentres ?? [])
    .filter(
      (c) =>
        (c.callCentreNumber ||
          c.callCentreName ||
          c.emergencyNumber ||
          c.notes) &&
        (c.callCentreNumber || c.callCentreName || "").trim() !== ""
    )
    .map((c) => ({
      name: c.callCentreName ? c.callCentreName.trim() : null,
      email: null as string | null, // no email field in UI for call centre yet
      phone: c.callCentreNumber ? c.callCentreNumber.trim() : null,
      notes: c.notes ? c.notes.trim() : null,
    }));

  return { reservations, accounts, callCentres };
}

/** 🔹 Map Create form → deep API body */
export function mapFormToCreateBody(
  form: SupplierFormData
): CreateSupplierBody {
  const { ownerFirstName, ownerLastName } = deriveOwnerNames(form.owner.name);

  const { reservations, accounts, callCentres } = mapFormContactsToDeep(form);

  const products = form.productIds ?? [];

  return {
    // Base supplier + user fields
    supplierCompanyName: form.supplierCompanyName,

    managerFirstName: form.managerFirstName,
    managerLastName: form.managerLastName,
    managerEmail: form.managerEmail,
    managerMobile: form.managerMobile,
    ownerFirstName,
    ownerLastName,

    // 🔹 Owner email is now the single source of truth for login creds
    ownerEmail: form.owner.email,
    ownerMobile: form.owner.mobile || undefined,

    companyWebsite: form.company.website || undefined,
    companyAddressLine1: form.company.addressLine1 || undefined,
    companyAddressLine2: form.company.addressLine2 || undefined,
    companyCity: form.company.city || undefined,
    companyState: form.company.state || undefined,
    companyCountry: form.company.country || undefined,
    companyPostalCode: form.company.pincode || undefined,

    loginNote: form.login.note || undefined,
    portalWebsite: form.company.website || undefined,
    portalLoginId: form.company.loginId || undefined,
    portalPassword: form.company.password || undefined,

    // Deep children
    products,
    reservations,
    accounts,
    callCentres,
  };
}

/** 🔹 Map Update form → deep API body, but do NOT send ownerUsername */
export function mapFormToUpdateBody(
  form: SupplierFormData
): UpdateSupplierBody {
  // Reuse create mapping + pass active flag for soft-delete toggle.
  return {
    ...(mapFormToCreateBody(form) as UpdateSupplierBody),
    active: !!form.active,
  };
}

/* ============================================================================
   Hook: state + logic for the listing page (ViewSuppliers)
============================================================================ */

export function useSuppliersPage(pageSize: number = 10) {
  // Search + filters
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState<SupplierFilters>({});

  // Pagination
  const [page, setPage] = useState(1);

  // Reset to first page on query/filter change
  useEffect(() => {
    setPage(1);
  }, [q, filters]);

  const queryQ = useMemo(() => {
    const parts = [
      q,
      filters.company,
      filters.product,
      filters.manager,
      filters.reservationEmail,
      filters.emergencyPhone,
      filters.addressLine1,
      filters.addressLine2,
      filters.pincode,
      filters.state,
    ]
      .map((x) => String(x ?? "").trim())
      .filter(Boolean);
    return parts.length ? parts.join(" ") : undefined;
  }, [
    q,
    filters.company,
    filters.product,
    filters.manager,
    filters.reservationEmail,
    filters.emergencyPhone,
    filters.addressLine1,
    filters.addressLine2,
    filters.pincode,
    filters.state,
  ]);

  // --- React Query: list suppliers from API ---
  const { data, isLoading, isError, refetch } = useSuppliers({
    page,
    limit: pageSize,
    q: queryQ,
    country: filters.country,
    city: filters.city,
    status: (filters.status as any) || undefined,
    includeDeleted: false,
  });

  const apiItems: SupplierResult[] = data?.items ?? [];
  const total = data?.total ?? apiItems.length;

  // Map to UI rows
  const rows: SupplierRow[] = useMemo(
    () => apiItems.map(mapSupplierToRow),
    [apiItems]
  );

  const filtered: SupplierRow[] = rows;

  const totalPages = Math.max(
    1,
    Math.ceil((data?.total ?? rows.length) / pageSize)
  );
  const start = (page - 1) * pageSize;
  const serialStart = start;

  const pageRows = rows;

  /* -------------------------- Delete (API) ---------------------------- */

  const deleteMutation = useDeleteSupplierMutation();
  const approveMutation = useApproveSupplierMutation();
  const rejectMutation = useRejectSupplierMutation();

  const deleteSupplier = (id: number | string) => {
    deleteMutation.mutate(id, {
      onSuccess: () => {
        refetch();
      },
    });
  };

  const clearFilters = () => setFilters({});

  const approveSupplier = (id: number | string) => {
    approveMutation.mutate(id, {
      onSuccess: () => {
        refetch();
      },
    });
  };

  const rejectSupplier = (id: number | string) => {
    rejectMutation.mutate(id, {
      onSuccess: () => {
        refetch();
      },
    });
  };

  return {
    // Data
    rows,
    filtered,
    pageRows,
    serialStart,
    total,
    isLoading,
    isError,
    refetch,

    // Search
    q,
    setQ,

    // Filters
    filters,
    setFilters,
    clearFilters,

    // Pagination
    page,
    setPage,
    pageSize,
    totalPages,
    start,

    // Actions
    deleteSupplier,
    approveSupplier,
    rejectSupplier,
    isApproving: approveMutation.isPending,
    approvingId: (approveMutation.variables as number | string | undefined) ?? null,
    isRejecting: rejectMutation.isPending,
    rejectingId: (rejectMutation.variables as number | string | undefined) ?? null,
  };
}
